import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Alert,
    Autocomplete,
    Avatar,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Container,
    Drawer,
    Grid,
    IconButton,
    InputAdornment,
    LinearProgress,
    Pagination,
    Paper,
    Stack,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tabs,
    TextField,
    Typography,
    useTheme,
} from '@mui/material';
import {
    AutorenewRounded as AutorenewIcon,
    CheckCircle as CheckCircleIcon,
    Close as CloseIcon,
    Download as DownloadIcon,
    ErrorOutline as ErrorOutlineIcon,
    EventAvailable as EventAvailableIcon,
    FilterAlt as FilterAltIcon,
    LocalShipping as ShippingIcon,
    MonetizationOn as MonetizationIcon,
    People as PeopleIcon,
    PersonAdd as PersonAddIcon,
    PersonOff as PersonOffIcon,
    Receipt as ReceiptIcon,
    Refresh as RefreshIcon,
    RestartAlt as ResetIcon,
    Search as SearchIcon,
    Timeline as TimelineIcon,
    TrendingDown as TrendingDownIcon,
    TrendingUp as TrendingUpIcon,
    Workspaces as WorkspacesIcon,
} from '@mui/icons-material';
import {
    CartesianGrid,
    Cell,
    Legend,
    Line,
    LineChart,
    Pie,
    PieChart,
    ResponsiveContainer,
    Tooltip as RTooltip,
    XAxis,
    YAxis,
} from 'recharts';
import { useAuth } from '../context/AuthContext';
import config from '../config/config';

const COLORS = ['#1976d2', '#00bfa5', '#ffb300', '#ef5350', '#8e44ad', '#26a69a', '#3949ab'];

const DEFAULT_FILTERS = {
    startDate: null,
    endDate: null,
    iboNumber: '',
    planId: '',
    statuses: [],
    actionTypes: [],
    page: 0,
    size: 50,
    sortBy: 'actionTime',
    sortDirection: 'DESC',
};

const SUBSCRIPTION_ACTIONS = [
    'CREATED', 'PLAN_CHANGED', 'OPTION_CHANGED', 'RENEWED',
    'PAYMENT_SUCCEEDED', 'PAYMENT_FAILED', 'PAUSED', 'RESUMED',
    'CANCELED', 'EXPIRED', 'REACTIVATED', 'ITERATION_UNLOCKED',
    'SHIPMENT_DISPATCHED', 'SHIPMENT_DELIVERED',
];

const statusChipColor = (status) => {
    const s = (status || '').toUpperCase();
    if (['CREATED', 'PAYMENT_SUCCEEDED', 'RENEWED', 'REACTIVATED', 'RESUMED', 'SHIPMENT_DELIVERED'].includes(s)) return 'success';
    if (['CANCELED', 'EXPIRED', 'PAYMENT_FAILED'].includes(s)) return 'error';
    if (['PAUSED', 'PLAN_CHANGED', 'OPTION_CHANGED'].includes(s)) return 'warning';
    if (['SHIPMENT_DISPATCHED', 'ITERATION_UNLOCKED'].includes(s)) return 'info';
    return 'default';
};

const KpiCard = ({ icon: Icon, label, value, color }) => {
    const theme = useTheme();
    return (
        <Card
            elevation={0}
            sx={{
                borderRadius: 3,
                border: `1px solid ${theme.palette.divider}`,
                background: `linear-gradient(135deg, ${color}18 0%, ${color}06 100%)`,
                transition: 'transform .18s ease, box-shadow .18s ease',
                '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: `0 10px 26px -12px ${color}66`,
                },
            }}
        >
            <CardContent sx={{ py: 2.25, px: 2.5 }}>
                <Stack direction="row" alignItems="center" spacing={1.5}>
                    <Avatar sx={{ bgcolor: color, width: 40, height: 40 }}>
                        <Icon fontSize="small" />
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ letterSpacing: 0.4, textTransform: 'uppercase', fontWeight: 600 }}>
                            {label}
                        </Typography>
                        <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.2 }}>{value}</Typography>
                    </Box>
                </Stack>
            </CardContent>
        </Card>
    );
};

const SectionHeader = ({ icon: Icon, title, action }) => (
    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
        <Stack direction="row" alignItems="center" spacing={1.2}>
            {Icon && <Icon sx={{ color: 'primary.main' }} />}
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{title}</Typography>
        </Stack>
        {action}
    </Stack>
);

const SubscriptionEventsView = () => {
    const theme = useTheme();
    const { getAccessToken } = useAuth();
    const API_BASE_URL = config.API_BASE_URL;

    const [filters, setFilters] = useState(DEFAULT_FILTERS);
    const [activeTab, setActiveTab] = useState('dashboard');

    const [reportData, setReportData] = useState({ content: [], totalElements: 0, totalPages: 0 });
    const [analytics, setAnalytics] = useState(null);

    const [selectedTimeline, setSelectedTimeline] = useState(null);
    const [selectedSubscriptionId, setSelectedSubscriptionId] = useState(null);
    const [loadingTimeline, setLoadingTimeline] = useState(false);

    const [iboSearch, setIboSearch] = useState('');
    const [iboTimeline, setIboTimeline] = useState(null);
    const [loadingIboTimeline, setLoadingIboTimeline] = useState(false);

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [lastRefreshed, setLastRefreshed] = useState(null);

    const apiHeaders = useCallback(async () => {
        const token = await getAccessToken();
        if (!token) throw new Error('Authentication token not available');
        return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
    }, [getAccessToken]);

    const callApi = useCallback(async (path, { method = 'GET', body } = {}) => {
        const headers = await apiHeaders();
        const res = await fetch(`${API_BASE_URL}${path}`, {
            method, headers, body: body ? JSON.stringify(body) : undefined,
        });
        if (!res.ok) {
            const text = await res.text().catch(() => '');
            throw new Error(`${res.status} ${text}`);
        }
        const data = await res.json().catch(() => ({}));
        return data.data !== undefined ? data.data : data;
    }, [API_BASE_URL, apiHeaders]);

    const fetchAnalytics = useCallback(async () => {
        const data = await callApi('/api/v1/content/admin/subscription-audit/analytics', { method: 'POST', body: filters });
        setAnalytics(data);
    }, [callApi, filters]);

    const fetchReport = useCallback(async () => {
        const data = await callApi('/api/v1/content/admin/subscription-audit/report', { method: 'POST', body: filters });
        setReportData(data || { content: [], totalElements: 0, totalPages: 0 });
    }, [callApi, filters]);

    const refreshDashboard = useCallback(async () => {
        setLoading(true); setError(null);
        try {
            await fetchAnalytics();
            setLastRefreshed(new Date());
        } catch (err) {
            setError(`Failed to load analytics: ${err.message}`);
        } finally { setLoading(false); }
    }, [fetchAnalytics]);

    const refreshReport = useCallback(async () => {
        setLoading(true); setError(null);
        try {
            await fetchReport();
            setLastRefreshed(new Date());
        } catch (err) {
            setError(`Failed to load report: ${err.message}`);
        } finally { setLoading(false); }
    }, [fetchReport]);

    useEffect(() => {
        if (activeTab === 'dashboard') refreshDashboard();
        else if (activeTab === 'report') refreshReport();
    }, [activeTab, filters, refreshDashboard, refreshReport]);

    const handleRowClick = async (subscriptionId) => {
        if (!subscriptionId) return;
        setSelectedSubscriptionId(subscriptionId);
        setLoadingTimeline(true);
        setSelectedTimeline(null);
        try {
            const data = await callApi(`/api/v1/content/admin/subscription-audit/${subscriptionId}/timeline`);
            setSelectedTimeline(Array.isArray(data) ? data : (data?.events || []));
        } catch (err) {
            setError(`Failed to load timeline: ${err.message}`);
        } finally { setLoadingTimeline(false); }
    };

    const handleIboLookup = async () => {
        if (!iboSearch) return;
        setLoadingIboTimeline(true);
        setIboTimeline(null);
        try {
            const data = await callApi(`/api/v1/content/admin/subscription-audit/ibo/${iboSearch}/timeline`);
            setIboTimeline(Array.isArray(data) ? data : (data?.events || []));
        } catch (err) {
            setError(`Failed to load IBO timeline: ${err.message}`);
        } finally { setLoadingIboTimeline(false); }
    };

    const handleExport = async () => {
        try {
            setLoading(true);
            const headers = await apiHeaders();
            const res = await fetch(`${API_BASE_URL}/api/v1/content/admin/subscription-audit/export`, {
                method: 'POST', headers, body: JSON.stringify(filters),
            });
            if (!res.ok) throw new Error(`Export failed: ${res.status}`);
            const blob = await res.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `content-subscription-audit-${Date.now()}.csv`;
            document.body.appendChild(a); a.click(); document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
        } catch (err) {
            setError(err.message);
        } finally { setLoading(false); }
    };

    const handleFilter = (field, value) =>
        setFilters((prev) => ({ ...prev, [field]: value, page: 0 }));

    const formatCurrency = (v) =>
        new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(v || 0);
    const formatDate = (d) => (d ? new Date(d).toLocaleString() : '—');
    const toDateInput = (iso) => (iso ? iso.slice(0, 10) : '');

    const planDistributionData = useMemo(() => {
        if (!analytics?.planDistribution) return [];
        return Object.entries(analytics.planDistribution).map(([name, value]) => ({ name, value }));
    }, [analytics]);

    const mrrTrendData = useMemo(() => analytics?.mrrTrend || [], [analytics]);

    return (
        <Container maxWidth="xl" sx={{ py: 3 }}>
            {/* Header */}
            <Paper
                elevation={0}
                sx={{
                    p: 2.5,
                    mb: 2.5,
                    borderRadius: 3,
                    background: `linear-gradient(135deg, #7b1fa2 0%, #4a148c 100%)`,
                    color: 'common.white',
                }}
            >
                <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between" spacing={1.5}>
                    <Stack direction="row" alignItems="center" spacing={1.5}>
                        <Avatar sx={{ bgcolor: 'rgba(255,255,255,.18)' }}>
                            <AutorenewIcon />
                        </Avatar>
                        <Box>
                            <Typography variant="h5" sx={{ fontWeight: 700 }}>Subscription Events</Typography>
                            <Typography variant="body2" sx={{ opacity: 0.85 }}>
                                Every lifecycle event from the audit log: MRR, churn, payments & shipments
                            </Typography>
                        </Box>
                    </Stack>
                    <Stack direction="row" alignItems="center" spacing={1.5}>
                        {lastRefreshed && (
                            <Chip
                                size="small"
                                label={`Updated ${lastRefreshed.toLocaleTimeString()}`}
                                sx={{ bgcolor: 'rgba(255,255,255,.18)', color: 'inherit', fontWeight: 500 }}
                            />
                        )}
                        <Button
                            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
                            variant="contained"
                            disabled={loading}
                            onClick={() => (activeTab === 'dashboard' ? refreshDashboard() : refreshReport())}
                            sx={{ bgcolor: 'rgba(255,255,255,.2)', '&:hover': { bgcolor: 'rgba(255,255,255,.3)' } }}
                        >
                            Refresh
                        </Button>
                    </Stack>
                </Stack>
            </Paper>

            {/* Filters */}
            <Paper elevation={0} sx={{ p: 2, mb: 2.5, borderRadius: 3, border: `1px solid ${theme.palette.divider}` }}>
                <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1.5 }}>
                    <FilterAltIcon color="primary" fontSize="small" />
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Filters</Typography>
                </Stack>
                <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <TextField
                            fullWidth size="small" label="Start Date" type="date"
                            InputLabelProps={{ shrink: true }}
                            value={toDateInput(filters.startDate)}
                            onChange={(e) => handleFilter('startDate', e.target.value ? new Date(e.target.value).toISOString() : null)}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <TextField
                            fullWidth size="small" label="End Date" type="date"
                            InputLabelProps={{ shrink: true }}
                            value={toDateInput(filters.endDate)}
                            onChange={(e) => handleFilter('endDate', e.target.value ? new Date(e.target.value).toISOString() : null)}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <TextField
                            fullWidth size="small" label="IBO Number"
                            value={filters.iboNumber}
                            onChange={(e) => handleFilter('iboNumber', e.target.value)}
                            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <TextField fullWidth size="small" label="Plan ID"
                            value={filters.planId}
                            onChange={(e) => handleFilter('planId', e.target.value)} />
                    </Grid>
                    <Grid size={{ xs: 12, md: 10 }}>
                        <Autocomplete
                            multiple
                            size="small"
                            options={SUBSCRIPTION_ACTIONS}
                            value={filters.statuses}
                            onChange={(_, v) => handleFilter('statuses', v)}
                            renderTags={(value, getTagProps) =>
                                value.map((option, index) => (
                                    <Chip
                                        size="small"
                                        label={option}
                                        color={statusChipColor(option)}
                                        {...getTagProps({ index })}
                                        key={option}
                                    />
                                ))
                            }
                            renderInput={(params) => <TextField {...params} label="Action Types" />}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, md: 2 }}>
                        <Button fullWidth variant="outlined" startIcon={<ResetIcon />} onClick={() => setFilters(DEFAULT_FILTERS)}>
                            Reset
                        </Button>
                    </Grid>
                </Grid>
            </Paper>

            {loading && <LinearProgress sx={{ mb: 2, borderRadius: 2 }} />}
            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

            {/* Tabs */}
            <Paper elevation={0} sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}`, mb: 2.5 }}>
                <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} variant="scrollable" scrollButtons="auto" sx={{ px: 1.5 }}>
                    <Tab value="dashboard" icon={<TrendingUpIcon />} iconPosition="start" label="Dashboard" />
                    <Tab value="report" icon={<ReceiptIcon />} iconPosition="start" label="Detailed Report" />
                    <Tab value="ibo" icon={<PeopleIcon />} iconPosition="start" label="IBO Timeline" />
                </Tabs>
            </Paper>

            {activeTab === 'dashboard' && (
                <>
                    <Grid container spacing={2}>
                        <Grid size={{ xs: 6, md: 3 }}><KpiCard icon={CheckCircleIcon} label="Active Subscriptions" value={analytics?.activeSubscriptions ?? 0} color="#26a69a" /></Grid>
                        <Grid size={{ xs: 6, md: 3 }}><KpiCard icon={PersonAddIcon} label="New (period)" value={analytics?.newSubscriptions ?? 0} color="#1976d2" /></Grid>
                        <Grid size={{ xs: 6, md: 3 }}><KpiCard icon={PersonOffIcon} label="Canceled" value={analytics?.canceledSubscriptions ?? 0} color="#ef5350" /></Grid>
                        <Grid size={{ xs: 6, md: 3 }}><KpiCard icon={MonetizationIcon} label="MRR" value={formatCurrency(analytics?.mrr)} color="#8e44ad" /></Grid>
                        <Grid size={{ xs: 6, md: 3 }}>
                            <KpiCard icon={TrendingDownIcon} label="Churn Rate"
                                value={analytics?.churnRate != null ? `${Number(analytics.churnRate).toFixed(2)}%` : '—'}
                                color="#ffb300" />
                        </Grid>
                        <Grid size={{ xs: 6, md: 3 }}><KpiCard icon={EventAvailableIcon} label="ARPU" value={formatCurrency(analytics?.arpu)} color="#3949ab" /></Grid>
                        <Grid size={{ xs: 6, md: 3 }}><KpiCard icon={TrendingUpIcon} label="LTV" value={formatCurrency(analytics?.ltv)} color="#00bfa5" /></Grid>
                        <Grid size={{ xs: 6, md: 3 }}><KpiCard icon={ErrorOutlineIcon} label="Failed Payments" value={analytics?.failedPayments ?? 0} color="#d81b60" /></Grid>
                    </Grid>

                    <Grid container spacing={2} sx={{ mt: 0.5 }}>
                        <Grid size={{ xs: 12, md: 7 }}>
                            <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: `1px solid ${theme.palette.divider}`, height: '100%' }}>
                                <SectionHeader icon={TrendingUpIcon} title="MRR Trend" />
                                <ResponsiveContainer width="100%" height={280}>
                                    <LineChart data={mrrTrendData}>
                                        <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                                        <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                                        <YAxis tick={{ fontSize: 12 }} />
                                        <RTooltip formatter={(v) => formatCurrency(v)} />
                                        <Legend />
                                        <Line type="monotone" dataKey="mrr" stroke="#7b1fa2" name="MRR" strokeWidth={2} dot={{ r: 3 }} />
                                    </LineChart>
                                </ResponsiveContainer>
                            </Paper>
                        </Grid>
                        <Grid size={{ xs: 12, md: 5 }}>
                            <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: `1px solid ${theme.palette.divider}`, height: '100%' }}>
                                <SectionHeader icon={WorkspacesIcon} title="Plan Distribution" />
                                <ResponsiveContainer width="100%" height={280}>
                                    <PieChart>
                                        <Pie
                                            data={planDistributionData}
                                            cx="50%" cy="50%"
                                            innerRadius={55} outerRadius={95}
                                            paddingAngle={3}
                                            dataKey="value"
                                            label={(e) => `${e.name}: ${e.value}`}
                                        >
                                            {planDistributionData.map((_, i) => (
                                                <Cell key={i} fill={COLORS[i % COLORS.length]} />
                                            ))}
                                        </Pie>
                                        <RTooltip />
                                    </PieChart>
                                </ResponsiveContainer>
                            </Paper>
                        </Grid>
                    </Grid>

                    {analytics?.shipmentStats && (
                        <Paper elevation={0} sx={{ mt: 2, p: 2.5, borderRadius: 3, border: `1px solid ${theme.palette.divider}` }}>
                            <SectionHeader icon={ShippingIcon} title="Shipment Status" />
                            <Grid container spacing={2}>
                                {Object.entries(analytics.shipmentStats).map(([status, count], i) => (
                                    <Grid size={{ xs: 6, md: 3 }} key={status}>
                                        <KpiCard icon={ShippingIcon} label={status} value={count} color={COLORS[i % COLORS.length]} />
                                    </Grid>
                                ))}
                            </Grid>
                        </Paper>
                    )}
                </>
            )}

            {activeTab === 'report' && (
                <Paper elevation={0} sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}`, overflow: 'hidden' }}>
                    <Box sx={{ p: 2.5, pb: 1 }}>
                        <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between" spacing={1}>
                            <SectionHeader icon={ReceiptIcon} title="Subscription Audit Report" />
                            <Stack direction="row" alignItems="center" spacing={1.5}>
                                <Typography variant="caption" color="text.secondary">
                                    Showing {reportData.content.length} of {reportData.totalElements} rows
                                </Typography>
                                <Button variant="outlined" startIcon={<DownloadIcon />} onClick={handleExport} disabled={loading}>
                                    Export CSV
                                </Button>
                            </Stack>
                        </Stack>
                    </Box>
                    <TableContainer>
                        <Table size="small">
                            <TableHead sx={{ bgcolor: 'action.hover' }}>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 700 }}>Time</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>IBO</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Plan</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Action</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Subscription ID</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 700 }}>Amount</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Square Sub</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {reportData.content.map((row) => (
                                    <TableRow
                                        key={row.id}
                                        hover
                                        onClick={() => handleRowClick(row.subscriptionId)}
                                        sx={{ cursor: row.subscriptionId ? 'pointer' : 'default' }}
                                    >
                                        <TableCell>{formatDate(row.actionTime)}</TableCell>
                                        <TableCell>{row.iboNumber || '—'}</TableCell>
                                        <TableCell>
                                            {row.planId || '—'}
                                            {row.planOptionId ? ` / ${row.planOptionId}` : ''}
                                        </TableCell>
                                        <TableCell>
                                            <Chip size="small" label={row.actionType} color={statusChipColor(row.actionType)} variant="outlined" />
                                        </TableCell>
                                        <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{row.subscriptionId || '—'}</TableCell>
                                        <TableCell align="right">{formatCurrency(row.amount)}</TableCell>
                                        <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{row.squareSubscriptionId || '—'}</TableCell>
                                    </TableRow>
                                ))}
                                {reportData.content.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.disabled' }}>
                                            No records found
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    {reportData.totalPages > 1 && (
                        <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
                            <Pagination
                                page={filters.page + 1}
                                count={reportData.totalPages}
                                onChange={(_, p) => handleFilter('page', p - 1)}
                                color="primary"
                                shape="rounded"
                            />
                        </Box>
                    )}
                </Paper>
            )}

            {activeTab === 'ibo' && (
                <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: `1px solid ${theme.palette.divider}` }}>
                    <SectionHeader icon={PeopleIcon} title="Lookup Subscription History by IBO" />
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 2 }}>
                        <TextField
                            size="small"
                            label="IBO Number"
                            value={iboSearch}
                            onChange={(e) => setIboSearch(e.target.value)}
                            sx={{ minWidth: 260 }}
                            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
                        />
                        <Button
                            variant="contained"
                            onClick={handleIboLookup}
                            disabled={loadingIboTimeline || !iboSearch}
                            startIcon={loadingIboTimeline ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />}
                        >
                            Lookup
                        </Button>
                    </Stack>

                    {iboTimeline && (
                        <Stack spacing={1.5}>
                            {iboTimeline.map((ev, idx) => (
                                <Paper
                                    key={ev.id || idx}
                                    elevation={0}
                                    sx={{
                                        p: 1.5,
                                        borderRadius: 2,
                                        borderLeft: `4px solid`,
                                        borderLeftColor: `${statusChipColor(ev.actionType)}.main`,
                                        bgcolor: 'action.hover',
                                    }}
                                >
                                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                                        <Chip size="small" label={ev.actionType} color={statusChipColor(ev.actionType)} />
                                        <Typography variant="caption" color="text.secondary">
                                            {formatDate(ev.actionTime)} • Sub: {ev.subscriptionId || '—'}
                                        </Typography>
                                    </Stack>
                                    {ev.amount != null && (
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                            {formatCurrency(ev.amount)}
                                        </Typography>
                                    )}
                                </Paper>
                            ))}
                            {iboTimeline.length === 0 && (
                                <Typography variant="body2" color="text.disabled" sx={{ textAlign: 'center', py: 3 }}>
                                    No events for this IBO
                                </Typography>
                            )}
                        </Stack>
                    )}
                </Paper>
            )}

            {/* Subscription timeline drawer */}
            <Drawer
                anchor="right"
                open={!!selectedSubscriptionId}
                onClose={() => { setSelectedSubscriptionId(null); setSelectedTimeline(null); }}
                PaperProps={{ sx: { width: { xs: '100%', md: 520 } } }}
            >
                <Box sx={{ p: 2.5 }}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                        <Stack direction="row" alignItems="center" spacing={1.2}>
                            <Avatar sx={{ bgcolor: '#7b1fa2' }}><TimelineIcon /></Avatar>
                            <Box>
                                <Typography variant="h6" sx={{ fontWeight: 700 }}>Subscription Timeline</Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                                    {selectedSubscriptionId}
                                </Typography>
                            </Box>
                        </Stack>
                        <IconButton onClick={() => { setSelectedSubscriptionId(null); setSelectedTimeline(null); }}>
                            <CloseIcon />
                        </IconButton>
                    </Stack>

                    {loadingTimeline ? (
                        <Stack alignItems="center" sx={{ py: 4 }}><CircularProgress /></Stack>
                    ) : (
                        <Stack spacing={1.5}>
                            {(selectedTimeline || []).map((ev, idx) => (
                                <Paper
                                    key={ev.id || idx}
                                    elevation={0}
                                    sx={{
                                        p: 1.5,
                                        borderRadius: 2,
                                        borderLeft: `4px solid`,
                                        borderLeftColor: `${statusChipColor(ev.actionType)}.main`,
                                        bgcolor: 'action.hover',
                                    }}
                                >
                                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                                        <Chip size="small" label={ev.actionType} color={statusChipColor(ev.actionType)} />
                                        <Typography variant="caption" color="text.secondary">
                                            {formatDate(ev.actionTime)} • {ev.actionBy || 'SYSTEM'}
                                        </Typography>
                                    </Stack>
                                    {ev.amount != null && (
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                            {formatCurrency(ev.amount)}
                                        </Typography>
                                    )}
                                    {ev.details && (
                                        <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                                            {typeof ev.details === 'string' ? ev.details : JSON.stringify(ev.details)}
                                        </Typography>
                                    )}
                                </Paper>
                            ))}
                            {(!selectedTimeline || selectedTimeline.length === 0) && (
                                <Typography variant="body2" color="text.disabled" sx={{ textAlign: 'center', py: 3 }}>
                                    No events found
                                </Typography>
                            )}
                        </Stack>
                    )}
                </Box>
            </Drawer>
        </Container>
    );
};

export default SubscriptionEventsView;
